import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, Briefcase, User, MessageSquare, MessagesSquare, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUnreadCounts } from "@/hooks/useUnreadCounts";
import { ROUTES } from "@/lib/constants";
import { isNavItemActive } from "./navActive";

interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: string;
}

// Five tabs for everyone, admins included: a sixth (Admin) squeezed every
// label at phone width, and "Administración" didn't fit at all. Admins reach
// the dashboard from the account menu instead (TopBar), and from the sidebar
// on desktop.
const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.ASSISTANT, icon: MessageSquare, labelKey: "nav.assistant" },
  { to: ROUTES.CHATS, icon: MessagesSquare, labelKey: "nav.chats" },
  { to: ROUTES.DISCOVER, icon: Compass, labelKey: "nav.discover" },
  { to: ROUTES.WORK, icon: Briefcase, labelKey: "nav.myWork" },
  { to: ROUTES.PROFILE, icon: User, labelKey: "nav.profile" },
];

function BottomNav() {
  const { t } = useTranslation();
  const { unreadChats } = useUnreadCounts();
  const { pathname } = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border-default bg-surface pb-safe md:hidden">
      <div className="flex h-16 items-center justify-around">
        {NAV_ITEMS.map((item) => {
          const isActive = isNavItemActive(item.to, pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex flex-col items-center gap-0.5 text-xs transition-colors",
                isActive
                  ? "font-semibold text-text-primary"
                  : "text-text-muted hover:text-text-secondary",
              )}
            >
              <>
                {/* The current tab sits in a yellow pill, like the landing's Get started. */}
                <span className={cn("relative rounded-full px-3 py-0.5 transition-colors", isActive && "bg-cta")}>
                  <item.icon
                    className={cn(
                      "h-5 w-5",
                      isActive ? "text-ink" : "text-text-muted",
                    )}
                  />
                  {item.to === ROUTES.CHATS && unreadChats > 0 && (
                    <span
                      aria-label={t("nav.unreadBadge", "{{count}} unread chats", { count: unreadChats })}
                      className="absolute -right-2 -top-1.5 min-w-4 rounded-full bg-brand-500 px-1 text-[10px] font-semibold leading-4 text-white"
                    >
                      {unreadChats > 99 ? "99+" : unreadChats}
                    </span>
                  )}
                </span>
                <span>{t(item.labelKey)}</span>
              </>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export { BottomNav };
